import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { SectionHeader } from '../SectionHeader/SectionHeader';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

const STORY_PARAGRAPHS = [
  {
    id: 'about.story.p1',
    defaultMessage:
      'Every concert, match, festival or workshop begins with the same feeling: people with an interest, looking for an experience to live. That is where Ticket Zone began.',
  },
  {
    id: 'about.story.p2',
    defaultMessage:
      'A platform that gathers events in one place, and makes discovering and booking them easier, faster and closer to everyone.',
  },
  {
    id: 'about.story.p3',
    defaultMessage:
      'Today, we are a space that connects people to the moments they love — from music and sports to culture and new experiences.',
  },
] as const;

const SUMMARY_CARDS = [
  {
    id: 'events',
    title: { id: 'about.story.card.events.title', defaultMessage: 'Events' },
    sublabel: {
      id: 'about.story.card.events.sublabel',
      defaultMessage: 'Bringing every interest together',
    },
  },
  {
    id: 'community',
    title: { id: 'about.story.card.community.title', defaultMessage: 'Community' },
    sublabel: { id: 'about.story.card.community.sublabel', defaultMessage: 'Growing every day' },
  },
] as const;

const SummaryCard = ({ title, sublabel }: { title: ReactNode; sublabel: ReactNode }) => (
  <Flex
    flex='1'
    dir='rtl'
    direction='column'
    // RTL: stretch text blocks full-width and right-align with textAlign='start'
    // (in RTL, 'start' = right; 'end' would resolve to the left edge).
    align='stretch'
    justify='center'
    textAlign='start'
    gap='8px'
    minH={{ base: '104px', md: '132px' }}
    px={{ base: '24px', md: '32px' }}
    py={{ base: '20px', md: '24px' }}
    bg='white'
    border='1px solid'
    borderColor='dark.D100'
    borderRadius='20px'
  >
    <Text
      as='p'
      m={0}
      color='#1B1917' // text-primary
      fontFamily={FONT_FAMILY}
      fontWeight='800'
      fontSize={{ base: '26px', md: '32px' }}
      lineHeight='1.1'
    >
      {title}
    </Text>
    <Text
      as='p'
      m={0}
      color='dark.D500'
      fontFamily={FONT_FAMILY}
      fontWeight='400'
      fontSize={{ base: '15px', md: '16px' }}
      lineHeight='1.2'
    >
      {sublabel}
    </Text>
  </Flex>
);

/**
 * About page Story section (TZ-385): the "قصتنا" header + story copy as a two-column
 * block (header on the start side, paragraphs on the other) collapsing to a single
 * stacked column on mobile, followed by two summary cards (events / community). RTL.
 */
export const StorySection = () => (
  <Box as='section' dir='rtl' bg='white' aria-labelledby='about-story-heading'>
    <VStack
      w='100%'
      maxW='container.xl'
      mx='auto'
      px={{ base: '16px', md: '100px' }}
      py={{ base: '48px', md: '80px' }}
      spacing={{ base: '40px', md: '64px' }}
      align='stretch'
    >
      <Flex
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: '24px', md: '64px', lg: '96px' }}
        align='flex-start'
      >
        <Box flex='1'>
          <SectionHeader
            titleId='about-story-heading'
            titleMaxW={{ base: '353px', md: 'none' }}
            eyebrow={<FormattedMessage id='about.story.eyebrow' defaultMessage='Our story' />}
            title={
              <FormattedMessage
                id='about.story.title'
                defaultMessage='We started from a passion for moments <strong>and grew with every experience</strong>'
                values={{
                  strong: (chunks) => (
                    <Box as='span' fontWeight='900'>
                      {chunks}
                    </Box>
                  ),
                }}
              />
            }
          />
        </Box>

        <VStack flex='1' align='stretch' textAlign='start' spacing={{ base: '16px', md: '20px' }}>
          {STORY_PARAGRAPHS.map(({ id, defaultMessage }) => (
            <Text
              key={id}
              as='p'
              m={0}
              color='dark.D500'
              fontFamily={FONT_FAMILY}
              fontWeight='400'
              fontSize={{ base: '15px', md: '16px' }}
              lineHeight='175%'
            >
              <FormattedMessage id={id} defaultMessage={defaultMessage} />
            </Text>
          ))}
        </VStack>
      </Flex>

      {/* dir='ltr' so the cards keep document order across the row (events on the left,
          community on the right per the design) while each card stays RTL internally;
          on mobile they stack events-first. */}
      <Flex dir='ltr' direction={{ base: 'column', md: 'row' }} gap={{ base: '16px', md: '24px' }}>
        {SUMMARY_CARDS.map(({ id, title, sublabel }) => (
          <SummaryCard
            key={id}
            title={<FormattedMessage id={title.id} defaultMessage={title.defaultMessage} />}
            sublabel={
              <FormattedMessage id={sublabel.id} defaultMessage={sublabel.defaultMessage} />
            }
          />
        ))}
      </Flex>
    </VStack>
  </Box>
);
